import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, Instagram, MessageCircle } from 'lucide-react';
import { INSTAGRAM_URL, INSTAGRAM_DM_URL, INSTAGRAM_HANDLE } from './data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  'Festival / Club',
  'Evento Corporativo',
  'Fiesta privada',
  'Otro',
];

const EMPTY = { name: '', eventType: '', date: '', location: '', message: '' };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.eventType || !form.message) {
      toast.error('Rellena al menos tu nombre, tipo de evento y tu idea.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('¡Mensaje enviado! Alfredj te responderá muy pronto.');
      setForm(EMPTY);
    } catch (err) {
      toast.error('Ups, algo falló. Escríbeme por Instagram mientras lo arreglo.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-0 border-b border-white/20 py-3 text-lg text-[#f1f0ea] placeholder:text-[#f1f0ea]/30 focus:outline-none focus:border-acid transition-colors';

  return (
    <section id="contacto" data-testid="contact-section" className="relative py-20 sm:py-32 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_dj-alfredj/artifacts/qq8lwpdu_alfredj1.webp"
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-[0.14] saturate-[0.6]"
        />
        <div className="absolute inset-0 bg-ink/80" />
      </div>
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-acid uppercase tracking-[0.3em] text-xs mb-4">El cierre</p>
          <h2 className="font-heading uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-[#f1f0ea]">
            Cuéntame tu <span className="text-stroke-acid">idea</span> y hagámosla sonar
          </h2>
          <p className="mt-6 text-[#f1f0ea]/70 font-light max-w-md">
            Directo y al grano. Rellena el formulario con los datos de tu evento y te respondo con
            disponibilidad y presupuesto sin compromiso.
          </p>
          <div className="mt-8 font-heading uppercase text-2xl text-[#f1f0ea]/80">
            Zona: <span className="text-acid">Albacete</span> y alrededores
          </div>

          <div className="mt-10 border border-white/10 bg-surface/60 p-6" data-testid="contact-instagram-block">
            <div className="flex items-center gap-3">
              <Instagram className="text-acid" size={26} />
              <div>
                <p className="font-heading uppercase text-xl text-[#f1f0ea] leading-none">
                  ¿Prefieres Instagram?
                </p>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#f1f0ea]/60 hover:text-acid transition-colors"
                >
                  @{INSTAGRAM_HANDLE}
                </a>
              </div>
            </div>
            <p className="mt-4 text-sm text-[#f1f0ea]/60 font-light">
              La forma más rápida de hablar conmigo. Escríbeme un mensaje directo y te respondo con la disponibilidad.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <a
                href={INSTAGRAM_DM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-dm"
                className="inline-flex items-center justify-center gap-2 bg-acid text-white font-heading uppercase text-sm px-5 py-3 hover:-translate-y-0.5 transition-transform"
              >
                <MessageCircle size={18} /> Enviar mensaje directo
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-profile"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-[#f1f0ea] font-heading uppercase text-sm px-5 py-3 hover:border-acid hover:text-acid transition-colors"
              >
                <Instagram size={18} /> Ver perfil
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-7"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-[#f1f0ea]/50">Nombre</label>
            <input
              data-testid="contact-name"
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Tu nombre"
              className={inputCls}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#f1f0ea]/50">Tipo de evento</label>
            <select
              data-testid="contact-event-type"
              value={form.eventType}
              onChange={set('eventType')}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              <option value="" className="bg-ink text-[#f1f0ea]">Selecciona…</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t} className="bg-ink text-[#f1f0ea]">
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#f1f0ea]/50">Fecha</label>
              <input
                data-testid="contact-date"
                type="date"
                value={form.date}
                onChange={set('date')}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#f1f0ea]/50">Lugar</label>
              <input
                data-testid="contact-location"
                type="text"
                value={form.location}
                onChange={set('location')}
                placeholder="Ciudad / sala"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#f1f0ea]/50">Cuéntame tu idea</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={set('message')}
              rows={4}
              placeholder="¿Qué evento tienes en mente? ¿Qué grupos te gustan?"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-acid text-white font-heading uppercase text-lg px-8 py-4 hover:-translate-y-1 hover:shadow-[5px_5px_0px_#f1f0ea] transition-all duration-200 disabled:opacity-60 disabled:translate-y-0 disabled:shadow-none"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            {loading ? 'Enviando…' : 'Enviar solicitud'}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
